import type { InternalPermission } from "./InternalPermissions";
import {
  type CustomPermissions,
  GenericPermission,
  makePermission,
  type ValidProjectSpecificPermissions,
} from "./PermissionsByRole";

/**
 * Check for direct entity-level permission only.
 */
export function hasEntityPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  requiredPermission: GenericPermission,
  entityType: EntityType,
): boolean {
  const validEntityPermission = makePermission({
    permission: requiredPermission,
    entityType,
  });
  if (permissions.has(validEntityPermission)) {
    return true;
  }

  return false;
}

export function hasAtomPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  atomType: AtomType,
  entityType: EntityType,
  requiredPermission: GenericPermission,
): boolean {
  const validAtomPermission = makePermission({
    permission: requiredPermission,
    entityType,
    atomType,
  });
  // if we have the required permission for the entity, we also have it for the atom
  const validEntityPermission = makePermission({
    permission: requiredPermission,
    entityType,
  });

  // If checking for Update/Create/Delete Children permission, also check the base permission
  const validAtomBasePermission =
    requiredPermission === GenericPermission.UpdateChildren ||
    requiredPermission === GenericPermission.CreateChildren ||
    requiredPermission === GenericPermission.DeleteChildren
      ? makePermission({
          permission: GenericPermission.Update,
          entityType,
          atomType,
        })
      : null;
  const validEntityBasePermission =
    requiredPermission === GenericPermission.UpdateChildren ||
    requiredPermission === GenericPermission.CreateChildren ||
    requiredPermission === GenericPermission.DeleteChildren
      ? makePermission({
          permission: GenericPermission.Update,
          entityType,
        })
      : null;

  if (
    permissions.has(validAtomPermission) ||
    permissions.has(validEntityPermission) ||
    (validAtomBasePermission && permissions.has(validAtomBasePermission)) ||
    (validEntityBasePermission && permissions.has(validEntityBasePermission))
  ) {
    return true;
  }
  return false;
}

type ParsedProjectPermission = {
  entityType: string;
  atomType?: string;
  field?: string;
  permission: GenericPermission;
};

const genericPermissionValues = new Set<string>(
  Object.values(GenericPermission),
);

function parseProjectPermission(
  requiredPermission: string,
): ParsedProjectPermission | null {
  const separatorIndex = requiredPermission.lastIndexOf("/");
  if (separatorIndex === -1) {
    return null;
  }

  const scope = requiredPermission.slice(0, separatorIndex);
  const permission = requiredPermission.slice(separatorIndex + 1);
  if (!genericPermissionValues.has(permission)) {
    return null;
  }

  const [entityType, atomType, ...fieldParts] = scope.split(":");
  if (!entityType) {
    return null;
  }

  return {
    entityType,
    atomType,
    field: fieldParts.length > 0 ? fieldParts.join(":") : undefined,
    permission: permission as GenericPermission,
  };
}

/**
 * Field permission check with standard fallback:
 * entity -> atom -> field.
 */
export function hasFieldPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  requiredPermission: GenericPermission,
  entityType: EntityType,
  atomType: AtomType,
  field: string,
): boolean {
  let entityPermission = requiredPermission;
  if (requiredPermission === GenericPermission.CreateChildren) {
    entityPermission = GenericPermission.Create;
  } else if (requiredPermission === GenericPermission.UpdateChildren) {
    entityPermission = GenericPermission.Update;
  } else if (requiredPermission === GenericPermission.DeleteChildren) {
    entityPermission = GenericPermission.Delete;
  }

  // if we have the required permission for the entity, we also have it for the field
  const validEntityPermission = makePermission({
    permission: entityPermission,
    entityType,
  });
  // if we have the required permission for the atom, we also have it for the field
  const validAtomPermission =
    requiredPermission === GenericPermission.Create ||
    requiredPermission === GenericPermission.Delete ||
    requiredPermission === GenericPermission.Rename ||
    requiredPermission === GenericPermission.Move
      ? undefined
      : makePermission({
          permission: requiredPermission,
          entityType,
          atomType,
        });

  const validFieldPermission = makePermission({
    permission: requiredPermission,
    entityType,
    atomType,
    field,
  });

  if (
    permissions.has(validEntityPermission) ||
    (validAtomPermission !== undefined &&
      permissions.has(validAtomPermission)) ||
    permissions.has(validFieldPermission)
  ) {
    return true;
  }
  return false;
}

/**
 * Check one serialized project permission using the same fallback semantics as
 * backend transaction checks:
 *
 * - entity permissions are direct only
 * - atom permissions can be satisfied by atom or entity permissions
 * - field permissions can be satisfied by field, atom or entity permissions
 * - reducer/custom permissions remain exact string matches
 */
export function hasProjectPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  requiredPermission: ValidProjectSpecificPermissions<
    EntityType,
    AtomType,
    ReducerPermissions
  >,
): boolean {
  if (permissions.has(requiredPermission)) {
    return true;
  }

  const parsed = parseProjectPermission(requiredPermission);
  if (!parsed) {
    return false;
  }

  if (parsed.field && parsed.atomType) {
    return hasFieldPermission<EntityType, AtomType, ReducerPermissions>(
      permissions,
      parsed.permission,
      parsed.entityType as EntityType,
      parsed.atomType as AtomType,
      parsed.field,
    );
  }

  if (parsed.atomType) {
    return hasAtomPermission<EntityType, AtomType, ReducerPermissions>(
      permissions,
      parsed.atomType as AtomType,
      parsed.entityType as EntityType,
      parsed.permission,
    );
  }

  return hasEntityPermission<EntityType, AtomType, ReducerPermissions>(
    permissions,
    parsed.permission,
    parsed.entityType as EntityType,
  );
}

/**
 * Field permission check without entity/atom fallback.
 * Used by `@requireExplicitWritePermission`-tagged fields.
 */
export function hasStrictFieldPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  requiredPermission: GenericPermission,
  entityType: EntityType,
  atomType: AtomType,
  field: string,
): boolean {
  const validFieldPermission = makePermission({
    permission: requiredPermission,
    entityType,
    atomType,
    field,
  });

  return permissions.has(validFieldPermission);
}

/**
 * Check for a single custom permission string (`custom:<Name>`).
 */
export function hasCustomPermission<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  customPermission: CustomPermissions,
): boolean {
  return permissions.has(customPermission);
}

export function hasPermissionToExecuteReducer<
  EntityType extends string,
  AtomType extends string,
  ReducerPermissions extends string,
>(
  _permissions: Set<
    | ValidProjectSpecificPermissions<EntityType, AtomType, ReducerPermissions>
    | InternalPermission
  >,
  _requiredPermission: GenericPermission,
  _entityType: EntityType,
  _atomType: AtomType,
): boolean {
  return false;
}
